import { Link, useNavigate } from '@tanstack/react-router';
import type { CityDto, SourceChannelDto, SourceMessageDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../shared/components/empty-state';
import {
  formatAutoPublishSkipReasonBadge,
  formatConfidence,
  formatPostedAt,
  formatSimilarity,
  IMPORT_STATUS_VARIANT,
  type ImportQueueT,
} from '../lib/format';
import type { ImportQueueFiltersState } from './import-queue-filters';

interface Props {
  items: ReadonlyArray<SourceMessageDto>;
  cities: ReadonlyArray<CityDto>;
  channels: ReadonlyArray<SourceChannelDto>;
  nextCursor: string | null;
  currentCursor?: string | undefined;
  currentSearch: ImportQueueFiltersState & { cursor?: string | undefined };
}

const confidenceClass: Record<'high' | 'mid' | 'low' | 'none', string> = {
  high: 'border-emerald-500 text-emerald-700 dark:text-emerald-400',
  mid: 'border-amber-500 text-amber-700 dark:text-amber-400',
  low: 'border-rose-500 text-rose-700 dark:text-rose-400',
  none: '',
};

const skipReasonBadgeClass: Record<'secondary' | 'warning' | 'destructive', string> = {
  secondary: '',
  warning: 'border-amber-500 text-amber-700 dark:text-amber-400',
  destructive: '',
};

const importedClass = 'border-emerald-500 text-emerald-700 dark:text-emerald-400';
const duplicateClass = 'border-violet-500 text-violet-700 dark:text-violet-400';

export function ImportQueueTable({ items, cities, nextCursor, currentCursor, currentSearch }: Props) {
  const { t, i18n } = useTranslation('import-queue');
  const navigate = useNavigate();
  const cityById = new Map(cities.map((c) => [c.id, c]));

  if (items.length === 0 && !currentCursor) {
    return <EmptyState title={t('table.emptyTitle')} description={t('table.emptyDescription')} />;
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('table.headerChannel')}</TableHead>
            <TableHead>{t('table.headerCity')}</TableHead>
            <TableHead>{t('table.headerPostedAt')}</TableHead>
            <TableHead>{t('table.headerStatus')}</TableHead>
            <TableHead>{t('table.headerTitle')}</TableHead>
            <TableHead>{t('table.headerConfidence')}</TableHead>
            <TableHead className="text-right">{t('table.headerActions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const city = cityById.get(item.channel.cityId);
            const tz = city?.timezone ?? 'UTC';
            const confidence = formatConfidence(item.extracted?.confidence);
            return (
              <TableRow key={item.id}>
                <TableCell className="font-mono text-xs">
                  <a
                    href={`https://t.me/${item.channel.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    @{item.channel.slug}
                  </a>
                </TableCell>
                <TableCell>{city?.name ?? '—'}</TableCell>
                <TableCell className="tabular-nums text-sm">
                  {formatPostedAt(item.postedAt, tz, i18n.language)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant={IMPORT_STATUS_VARIANT[item.status]}
                    className={
                      item.status === 'IMPORTED'
                        ? importedClass
                        : item.status === 'DUPLICATE'
                          ? duplicateClass
                          : undefined
                    }
                  >
                    {t(`status.${item.status}`)}
                  </Badge>
                </TableCell>
                <TableCell className="max-w-[320px]">
                  <div className="truncate">{item.extracted?.fields.title ?? '—'}</div>
                  {item.autoPublishSkipReason &&
                    (() => {
                      const badge = formatAutoPublishSkipReasonBadge(
                        item.autoPublishSkipReason,
                        t as ImportQueueT,
                      );
                      return badge ? (
                        <Badge variant="outline" className={`mt-1 ${skipReasonBadgeClass[badge.variant]}`}>
                          {badge.label}
                        </Badge>
                      ) : null;
                    })()}
                  {item.duplicate != null && (
                    <Badge variant="outline" className={`mt-1 ${duplicateClass}`}>
                      {t('table.possibleDuplicate')}
                      {formatSimilarity(item.duplicate.similarity)}
                    </Badge>
                  )}
                  {!item.duplicate && (item.possibleDuplicates?.length ?? 0) > 0 && (
                    <Badge variant="outline" className={`mt-1 ${duplicateClass}`}>
                      {t('table.possibleDuplicate')}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {confidence.tone === 'none' ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <Badge variant="outline" className={confidenceClass[confidence.tone]}>
                      {confidence.label}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button asChild variant="ghost" size="sm">
                    <Link to="/import-queue/$messageId" params={{ messageId: item.id }}>
                      {t('table.open')}
                    </Link>
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {nextCursor && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() =>
              navigate({
                to: '/import-queue',
                search: () => {
                  const out: Record<string, string> = {};
                  for (const [k, v] of Object.entries(currentSearch)) {
                    if (k === 'cursor') continue;
                    if (v !== undefined && v !== '' && v !== null) out[k] = String(v);
                  }
                  out.cursor = nextCursor;
                  return out;
                },
              })
            }
          >
            {t('table.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
